import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import TechCard from '../components/Tech/TechCard';
import AdCard from '../components/AdCard/AdCard';
import SocialMediaLinks from '../components/SocialMediaLinks/SocialMediaLinks';
import { fetchTechArticleBySlug, fetchTechArticles } from '../api';
import { useAuth } from '../contexts/AuthContext';
import { resolveImageUrl } from '../utils/imageUrl';
import { applySocialMeta } from '../utils/socialMeta';
import { buildSharePreviewUrl } from '../utils/shareUrl';
import { buildSubscriberSignInPath, isSubscriberOnlyContent } from '../utils/subscriberAccess';
import { sanitizeHtml } from '../utils/sanitizeHtml';
import UcleusArticle from '../components/Tech/UcleusArticle';
import './TechDetail.css';

const TechDetail = () => {
  const { slug } = useParams();
  const [tech, setTech] = useState(null);
  const [relatedTechs, setRelatedTechs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { canAccessExclusiveContent } = useAuth();
  const signInPath = buildSubscriberSignInPath(`/tech/${slug}`);

  useEffect(() => {
    const loadTechAndRelated = async () => {
      try {
        setLoading(true);

        // Load the specific tech article
        const techData = await fetchTechArticleBySlug(slug);
        setTech(techData);

        // Load related tech articles
        const allTechsData = await fetchTechArticles();
        const allTechs = allTechsData.data || allTechsData;

        // Filter for related articles (same tags or recent articles)
        const related = allTechs
          .filter(t => t.slug !== slug) // Exclude current article
          .slice(0, 6); // Get 6 related articles

        setRelatedTechs(related);
        setError(null);
      } catch (err) {
        console.error('Failed to load tech article:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      loadTechAndRelated();
    }
  }, [slug]);

  useEffect(() => {
    if (!tech) return;

    const descriptionSource = tech.excerpt || tech.body || '';
    const plainDescription = descriptionSource
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 180);

    applySocialMeta({
      title: tech.meta_title || tech.title,
      description: tech.meta_description || plainDescription,
      url: window.location.href,
      image: tech.cover_image_url || tech.cover_image,
      type: 'article',
    });
  }, [tech]);

  // Function to strip HTML tags from content
  const stripHtml = (html) => {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Calculate reading time
  const getReadingTime = (content) => {
    const wordCount = stripHtml(content).split(' ').length;
    return Math.ceil(wordCount / 200); // Average reading speed
  };

  if (loading) {
    return (
      <div className="tech-detail-page">
        <div className="tech-detail-container">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading tech article...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !tech) {
    return (
      <div className="tech-detail-page">
        <div className="tech-detail-container">
          <div className="error-container">
            <h1>Article Not Found</h1>
            <p>The tech article you're looking for doesn't exist or has been moved.</p>
            <Link to="/tech" className="back-to-tech-btn">
              ← Back to Tech Insights
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const techCoverImageUrl = resolveImageUrl(tech.cover_image_url || tech.cover_image);
  const techShareUrl = tech.share_url || buildSharePreviewUrl('tech', tech.slug) || window.location.href;

  return (
    <div className="tech-detail-page">
      <div className="tech-detail-container">
        {/* Main Content */}
        <main className="tech-detail-main">
          {/* Breadcrumb */}
          <nav className="breadcrumb">
            <Link to="/">Home</Link>
            <span className="breadcrumb-separator">›</span>
            <Link to="/tech">Tech Insights</Link>
            <span className="breadcrumb-separator">›</span>
            <span className="breadcrumb-current">{tech.title}</span>
          </nav>

          {/* Article Header */}
          <header className="tech-detail-header">
            {/* Tags */}
            {tech.tags && tech.tags.length > 0 && (
              <div className="tech-detail-tags">
                {tech.tags.map((tag, index) => (
                  <span key={index} className="tech-detail-tag">
                    #{tag}
                  </span>
                ))}
              </div>
            )}

            {/* Title */}
            <h1 className="tech-detail-title">{tech.title}</h1>
            {isSubscriberOnlyContent(tech) && (
              <div className="tech-detail-meta">
                <div className="meta-left">
                  <span className="featured-badge">
                    <i className="fas fa-lock"></i>
                    Subscriber-only
                  </span>
                </div>
              </div>
            )}

            {/* Meta Info */}
            <div className="tech-detail-meta">
              <div className="meta-left">
                {tech.user && (
                  <span className="tech-author">
                    <i className="far fa-user"></i>
                    {tech.user.name}
                  </span>
                )}
                <span className="tech-date">
                  <i className="far fa-calendar-alt"></i>
                  {formatDate(tech.published_at || tech.created_at)}
                </span>
                <span className="tech-reading-time">
                  <i className="far fa-clock"></i>
                  {tech.reading_time || getReadingTime(tech.body)} min read
                </span>
              </div>
              <div className="meta-right">
                {tech.view_count > 0 && (
                  <span className="tech-views">
                    <i className="far fa-eye"></i>
                    {tech.view_count} views
                  </span>
                )}
                {tech.is_featured && (
                  <span className="featured-badge">
                    <i className="fas fa-star"></i>
                    Featured
                  </span>
                )}
                {tech.is_sticky && (
                  <span className="sticky-badge">
                    <i className="fas fa-thumbtack"></i>
                    Pinned
                  </span>
                )}
              </div>
            </div>
          </header>

          {/* Featured Image */}
          {techCoverImageUrl && (
            <div className="tech-detail-image">
              <img
                src={techCoverImageUrl}
                alt={tech.title}
                className="tech-featured-image"
              />
            </div>
          )}

          {/* Excerpt */}
          {tech.excerpt && (
            <div className="tech-detail-excerpt">
              <p>{tech.excerpt}</p>
            </div>
          )}

          {/* Article Content */}
          <article className="tech-detail-content">
            {isSubscriberOnlyContent(tech) ? (
              <div className="tech-content-body">
                <p>{tech.excerpt || 'This article is available to paid subscribers.'}</p>
                <p>
                  {canAccessExclusiveContent
                    ? 'Refresh this page if your subscription was just activated.'
                    : <>Subscriber access starts at $3.99 monthly or $38.50 yearly. <Link to={signInPath}>Sign in or start a paid subscription</Link>.</>}
                </p>
              </div>
            ) : (
              <div className="tech-content-body">
                {tech.body?.includes('data-ucleus-document') ? (
                  <UcleusArticle html={tech.body} title={tech.title} />
                ) : (
                  <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(tech.body) }} />
                )}
              </div>
            )}
          </article>

          {/* Social Media Links */}
          <div className="blog-social-section tech-social-section">
            <SocialMediaLinks
              episode={{
                ...tech,
                share_type: 'tech',
                share_url: techShareUrl,
              }}
            />
          </div>

          {/* Article Footer */}
          <footer className="tech-detail-footer">
            <div className="tech-footer-actions">
              <button
                className="share-btn"
                onClick={() =>
                  navigator.share?.({
                    title: tech.title,
                    text: techCoverImageUrl ? `${tech.excerpt || ''}\nCover image: ${techCoverImageUrl}` : (tech.excerpt || ''),
                    url: techShareUrl,
                  })
                }
              >
                <i className="fas fa-share-alt"></i>
                Share Article
              </button>
              <Link to="/tech" className="back-to-tech-btn">
                ← Back to Tech Insights
              </Link>
            </div>

            {/* Tags */}
            {tech.tags && tech.tags.length > 0 && (
              <div className="tech-footer-tags">
                <h4>Tags:</h4>
                <div className="footer-tags-list">
                  {tech.tags.map((tag, index) => (
                    <span key={index} className="footer-tag">
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </footer>

          {/* Related Articles */}
          {relatedTechs.length > 0 && (
            <section className="related-tech-section">
              <h3 className="section-title">
                <i className="fas fa-lightbulb"></i>
                Related Tech Articles
              </h3>
              <div className="related-tech-grid">
                {relatedTechs.slice(0, 3).map(relatedTech => (
                  <TechCard
                    key={relatedTech.id}
                    tech={relatedTech}
                    canAccessExclusiveContent={canAccessExclusiveContent}
                  />
                ))}
              </div>
            </section>
          )}
        </main>

        {/* Sidebar */}
        <aside className="tech-detail-sidebar">
          {/* Advertisement */}
          <div className="sidebar-section ad-section">
            <AdCard />
          </div>

          {/* Author Info */}
          {tech.user && (
            <div className="sidebar-section author-section">
              <h4>About the Author</h4>
              <div className="author-info">
                <div className="author-details">
                  <h5>{tech.user.name}</h5>
                  <p className="author-bio">
                    Technology enthusiast and writer passionate about digital innovation and emerging trends.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Recent Tech Articles */}
          <div className="sidebar-section recent-tech-section">
            <h4>Recent Tech Articles</h4>
            <div className="sidebar-tech-list">
              {relatedTechs.slice(0, 5).map(relatedTech => (
                <Link
                  key={relatedTech.id}
                  to={
                    isSubscriberOnlyContent(relatedTech) && !canAccessExclusiveContent
                      ? buildSubscriberSignInPath(`/tech/${relatedTech.slug}`)
                      : `/tech/${relatedTech.slug}`
                  }
                  className="sidebar-tech-item"
                >
                  {resolveImageUrl(relatedTech.cover_image_url || relatedTech.cover_image) && (
                    <img
                      src={resolveImageUrl(relatedTech.cover_image_url || relatedTech.cover_image)}
                      alt={relatedTech.title}
                      className="sidebar-tech-image"
                    />
                  )}
                  <div className="sidebar-tech-content">
                    <h6>{relatedTech.title}</h6>
                    <p className="sidebar-tech-date">
                      {formatDate(relatedTech.published_at || relatedTech.created_at)}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>


        </aside>
      </div>
    </div>
  );
};

export default TechDetail;
